import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  IconUpload, 
  IconBuildingCommunity, 
  IconMail, 
  IconPhone, 
  IconWorld, 
  IconMapPin,
  IconAlertCircle,
  IconInfoCircle
} from '@tabler/icons-react';
import { organizationService } from '../services/api';
import { ROUTES } from '../constants/routes';
import styles from './OrganizationProfileForm.module.css';
import AddressAutocomplete from './AddressAutocomplete';
import { Coordinates } from '../types/event';

interface OrganizationProfileFormProps {
  organizationId?: number;
  isEditing?: boolean;
}

interface OrganizationFormData {
  name: string;
  description: string;
  email: string;
  phone: string;
  website: string;
  address: string;
  latitude?: number;
  longitude?: number;
  coordinates?: Coordinates;
  logoUrl: string;
  isActive: boolean;
}

const OrganizationProfileForm: React.FC<OrganizationProfileFormProps> = ({ 
  organizationId, 
  isEditing = false 
}) => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState<OrganizationFormData>({
    name: '',
    description: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    latitude: undefined,
    longitude: undefined,
    coordinates: undefined,
    logoUrl: '',
    isActive: true
  });
  
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  
  useEffect(() => {
    const fetchOrganizationData = async () => {
      if (isEditing && organizationId) {
        try {
          setLoading(true);
          const organization = await organizationService.getById(organizationId);
          setFormData({
            name: organization.name || '',
            description: organization.description || '',
            email: organization.email || '',
            phone: organization.phone || '',
            website: organization.website || '',
            address: organization.address || '',
            latitude: organization.latitude,
            longitude: organization.longitude,
            coordinates: organization.coordinates || undefined,
            logoUrl: organization.logoUrl || '',
            isActive: organization.isActive !== undefined ? organization.isActive : true
          });
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Erreur lors du chargement des données');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchOrganizationData();
  }, [organizationId, isEditing]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, isActive: e.target.checked }));
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      // Pour une future implémentation, on pourrait stocker le fichier
      // et l'utiliser pour mettre à jour l'aperçu ou l'uploader
      console.log('Logo file selected:', e.target.files[0].name);
    }
  };

  // Gestion du changement d'adresse via le composant d'autocomplete
  const handleAddressChange = (address: string) => {
    setFormData(prev => ({
      ...prev,
      address
    }));
  };

  // Gestion du changement de coordonnées
  const handleCoordinatesChange = (coordinates: Coordinates | null) => {
    console.log('Coordinates changed:', coordinates);
    setFormData(prev => ({
      ...prev,
      latitude: coordinates?.lat, 
      longitude: coordinates?.lon, 
      coordinates: coordinates || undefined 
    }));
  };

  // Préformater les URLs avec le protocole si nécessaire
  const formatUrl = (url: string) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `https://${url}`;
  };

  // Fonction de validation pour les URLs
  const validateUrl = (url: string): boolean => {
    if (!url) return true; // URLs vides sont valides (champs optionnels)
    try {
      // Essayer de créer un objet URL pour valider
      new URL(formatUrl(url));
      return true;
    } catch (e) {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    // Valider les URLs avant la soumission
    const websiteValid = validateUrl(formData.website);
    const logoUrlValid = validateUrl(formData.logoUrl);

    if (!websiteValid || !logoUrlValid) {
      setError(
        `Veuillez entrer des URLs valides: ${!websiteValid ? 'Site web' : ''} ${
          !logoUrlValid ? 'Logo URL' : ''
        }`
      );
      setSubmitting(false);
      return;
    }

    try {
      // TODO: Handle logo upload and get URL

      const payload = {
        ...formData,
        website: formatUrl(formData.website),
        logoUrl: formatUrl(formData.logoUrl),
        // Remove the coordinates object as it's not expected by the backend
        coordinates: undefined,
        // If we have a new logo file, we'd replace logoUrl with the new URL
        // For now, we keep the existing one or empty string
      };

      console.log('Form data before payload construction:', formData);
      console.log('Final payload with coordinates:', payload);

      console.log('Envoi des données:', JSON.stringify(payload, null, 2));

      if (isEditing && organizationId) {
        await organizationService.update(organizationId, payload);
        // Notification de succès
        alert('Profil mis à jour avec succès');
      } else {
        await organizationService.create(payload);
        // Notification de succès
        alert('Organisation créée avec succès');
      }

      // Navigate to the organization dashboard
      navigate(ROUTES.ORGANIZATION);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
      // Notification d'erreur
      alert(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Chargement des données...</p>
      </div>
    );
  }

  return (
    <div className={styles.formContainer}>
      <form onSubmit={handleSubmit}>
        <div className={styles.formSection}>
          <h2 className={styles.formTitle}>
            {isEditing ? 'Modifier le profil de l\'organisation' : 'Créer une nouvelle organisation'}
          </h2>
          
          {error && (
            <div className={styles.errorMessage}>
              <IconAlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}
          
          <div className={styles.avatarContainer}>
            <div className={styles.avatar}>
              {formData.logoUrl ? (
                <img 
                  src={formData.logoUrl} 
                  alt="Logo de l'organisation" 
                  className={styles.avatarImage}
                />
              ) : (
                <IconBuildingCommunity size={60} className={styles.avatarIcon} />
              )}
            </div>
            
            <div className={styles.fileInputWrapper}>
              <label htmlFor="logo-upload" className={styles.fileInputLabel}>
                <IconUpload size={14} />
                <span>Choisir un logo</span>
              </label>
              <input
                id="logo-upload"
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                onChange={handleLogoChange}
                className={styles.fileInput}
              />
              <button 
                type="button" 
                className={styles.imageInfoButton}
                onClick={() => {
                  const modal = document.getElementById('logoHelpModal');
                  if (modal) modal.style.display = 'flex';
                }}
                title="Comment ajouter un logo"
              >
                <IconInfoCircle size={16} />
              </button>
            </div>
          </div>
          
          <div className={styles.formField}>
            <label htmlFor="name" className={styles.label}>
              Nom de l'organisation<span className={styles.required}>*</span>
            </label>
            <div className={styles.inputWrapper}>
              <IconBuildingCommunity size={16} className={styles.inputIcon} />
              <input
                id="name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Nom de l'organisation"
                required
                className={styles.input}
              />
            </div>
          </div>
          
          <div className={styles.formField}>
            <label htmlFor="description" className={styles.label}>
              Description<span className={styles.required}>*</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Description de l'organisation, ses activités et sa mission"
              required
              className={styles.textarea}
              rows={3}
            />
          </div>
          
          <div className={styles.formRow}>
            <div className={styles.formField}>
              <label htmlFor="email" className={styles.label}>
                Email<span className={styles.required}>*</span>
              </label>
              <div className={styles.inputWrapper}>
                <IconMail size={16} className={styles.inputIcon} />
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email de contact"
                  required
                  className={styles.input}
                />
              </div>
            </div>
            
            <div className={styles.formField}>
              <label htmlFor="phone" className={styles.label}>
                Téléphone
              </label>
              <div className={styles.inputWrapper}>
                <IconPhone size={16} className={styles.inputIcon} />
                <input
                  id="phone"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Numéro de téléphone"
                  className={styles.input}
                />
              </div>
            </div>
          </div>
          
          <div className={styles.formField}>
            <label htmlFor="website" className={styles.label}>
              Site web
            </label>
            <div className={styles.inputWrapper}>
              <IconWorld size={16} className={styles.inputIcon} />
              <input
                id="website"
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://www.example.com"
                className={styles.input}
              />
            </div>
          </div>
          
          <div className={styles.formField}>
            <label htmlFor="address" className={styles.label}>
              Adresse<span className={styles.required}>*</span>
            </label>
            <div className={styles.inputWrapper}>
              <IconMapPin size={16} className={styles.inputIcon} />
              <AddressAutocomplete
                inputId="address"
                value={formData.address}
                onChange={handleAddressChange}
                onCoordinatesChange={handleCoordinatesChange}
                placeholder="Adresse postale complète"
                required
                country="ca"
                label=""
                className={styles.autocompleteWrapper}
                showMap={false}
              />
            </div>
          </div>
          
          <div className={styles.formField}>
            <label htmlFor="logoUrl" className={styles.label}>
              URL du logo
              <button 
                type="button" 
                className={styles.infoButton}
                onClick={() => {
                  const modal = document.getElementById('logoHelpModal');
                  if (modal) modal.style.display = 'flex';
                }}
                title="Comment trouver une URL de logo"
              >
                <IconInfoCircle size={16} />
              </button>
            </label>
            <div className={styles.inputWrapper}>
              <input
                id="logoUrl"
                type="url"
                name="logoUrl"
                value={formData.logoUrl}
                onChange={handleChange}
                placeholder="https://exemple.com/votre-logo.png"
                className={styles.input}
              />
            </div>
            <span className={styles.helpText}>Entrez l'URL directe vers votre image de logo</span>
          </div>
          
          {isEditing && (
            <div className={styles.formField}>
              <label className={styles.switchLabel}>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={handleSwitchChange}
                  className={styles.switchInput}
                />
                <span className={styles.switchSlider}></span>
                Organisation active
              </label>
            </div>
          )}
          
          <div className={styles.buttonGroup}>
            <button 
              type="button" 
              onClick={() => navigate(-1)}
              disabled={submitting}
              className={`${styles.button} ${styles.outlineButton}`}
            >
              Annuler
            </button>
            <button 
              type="submit" 
              disabled={submitting}
              className={styles.button}
            >
              {submitting ? 'Traitement en cours...' : (isEditing ? 'Mettre à jour' : 'Créer')}
            </button>
          </div>
        </div>
      </form>
      
      {/* Modal d'aide pour l'ajout de logo */}
      <div id="logoHelpModal" className={styles.helpModal}>
        <div className={styles.helpModalContent}>
          <div className={styles.helpModalHeader}>
            <h3>Comment ajouter un logo</h3>
            <button 
              type="button" 
              className={styles.closeModalButton}
              onClick={() => {
                const modal = document.getElementById('logoHelpModal');
                if (modal) modal.style.display = 'none';
              }}
            >
              ×
            </button>
          </div>
          <div className={styles.helpModalBody}>
            <h4>Pour télécharger un logo:</h4>
            <p>Actuellement, vous avez deux options pour ajouter un logo à votre organisation :</p>
            
            <h4>Option 1: Utiliser une URL d'image</h4>
            <ol>
              <li>Entrez directement l'URL de votre logo dans le champ "logoUrl" ci-dessous</li>
              <li>Assurez-vous que l'URL est directe et se termine par une extension d'image (.jpg, .png, .svg, etc.)</li>
              <li>L'image apparaîtra comme aperçu si l'URL est valide</li>
            </ol>
            
            <h4>Option 2: Héberger votre image en ligne</h4>
            <ol>
              <li>Téléchargez votre image sur un service comme <a href="https://imgur.com" target="_blank" rel="noopener noreferrer">Imgur</a> ou <a href="https://postimages.org" target="_blank" rel="noopener noreferrer">PostImages</a></li>
              <li>Copiez le lien direct vers l'image après l'avoir téléchargée</li>
              <li>Collez ce lien dans le champ "logoUrl"</li>
            </ol>
            
            <div className={styles.noteBox}>
              <p><strong>Astuce :</strong> Pour un meilleur résultat, utilisez une image carrée (1:1) de 512x512 pixels minimum.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationProfileForm;
